import React, { createContext, useContext, useEffect, useState } from 'react';

interface PWAContextType {
    installPrompt: any;
    isInstallable: boolean;
    isStandalone: boolean;
    isIOS: boolean;
    installApp: () => void;
    showIOSGuide: boolean;
    dismissIOSGuide: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export function PWAProvider({ children }: { children: React.ReactNode }) {
    const [installPrompt, setInstallPrompt] = useState<any>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);

    useEffect(() => {
        // Check for iOS
        const checkIOS = () => {
            const ua = window.navigator.userAgent.toLowerCase();
            return /iphone|ipad|ipod/.test(ua);
        };

        // Check for Safari (including iOS Safari)
        const checkSafari = () => {
            const ua = window.navigator.userAgent.toLowerCase();
            return /safari/.test(ua) && !/chrome/.test(ua) && !/chromium/.test(ua);
        };

        const iosDetected = checkIOS();
        const safariDetected = checkSafari();
        setIsIOS(iosDetected);

        // Check if already in standalone/installed mode
        const standalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true ||
            document.referrer.includes('android-app://');

        setIsStandalone(!!standalone);
        console.log('[PWA] Standalone check:', !!standalone);
        console.log('[PWA] iOS check:', iosDetected);
        console.log('[PWA] Safari check:', safariDetected);

        // On iOS Safari (not already installed), mark as installable
        // so the Install button appears with iOS-specific instructions
        if (iosDetected && !standalone) {
            setIsInstallable(true);
            console.log('[PWA] iOS Safari detected — install via Add to Home Screen');
        }

        // On macOS Safari, also mark as installable
        if (safariDetected && !iosDetected && !standalone) {
            setIsInstallable(true);
            console.log('[PWA] macOS Safari detected — install via Add to Dock');
        }

        // beforeinstallprompt — Chrome/Edge/Samsung browser
        const onBeforeInstall = (e: any) => {
            console.log('[PWA] beforeinstallprompt event fired! 🚀');
            e.preventDefault(); // suppress mini-infobar
            setInstallPrompt(e);
            setIsInstallable(true);
            console.log('[PWA] isInstallable set to true');
        };

        // appinstalled — user completed installation
        const onAppInstalled = () => {
            console.log('[PWA] App successfully installed! ✅');
            setIsInstallable(false);
            setIsStandalone(true);
            setInstallPrompt(null);
        };

        console.log('[PWA] Event listeners being attached...');

        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onAppInstalled);

        // Log service worker status
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((reg) => {
                console.log('[PWA] Service Worker ready:', reg.scope);
            });
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    const dismissIOSGuide = () => setShowIOSGuide(false);

    const installApp = async () => {
        // iOS / Safari — no native prompt, show guide instead
        if (isIOS || (!installPrompt && isInstallable)) {
            setShowIOSGuide(true);
            return;
        }

        if (!installPrompt) {
            console.warn('[PWA] No install prompt available');
            return;
        }

        try {
            installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            console.log('[PWA] User choice:', outcome);

            if (outcome === 'accepted') {
                // Only clear prompt after accepted — allow retry if dismissed
                setInstallPrompt(null);
                setIsInstallable(false);
            }
            // If dismissed → keep isInstallable true so button stays visible
        } catch (err) {
            console.error('[PWA] Install prompt error:', err);
        }
    };

    return (
        <PWAContext.Provider value={{
            installPrompt,
            isInstallable,
            isStandalone,
            isIOS,
            installApp,
            showIOSGuide,
            dismissIOSGuide,
        }}>
            {children}
        </PWAContext.Provider>
    );
}

export const usePWA = () => {
    const context = useContext(PWAContext);
    if (context === undefined) {
        throw new Error('usePWA must be used within a PWAProvider');
    }
    return context;
};
